
import React, { lazy, Suspense, useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Mail, ShieldCheck, Users, Brain, AlertCircle, Lightbulb, Clock } from 'lucide-react';
import NavBar from '@/components/NavBar';
import MainFooter from '@/components/MainFooter';
import { 
  Card, 
  CardHeader, 
  CardTitle, 
  CardDescription, 
  CardContent, 
  CardFooter 
} from '@/components/ui/card';
import { 
  Tabs, 
  TabsContent, 
  TabsList, 
  TabsTrigger 
} from '@/components/ui/tabs';
import { 
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

// Lazy load the image sections
const AIResponsibilityImage = lazy(() => import('../components/responsibleAI/AIResponsibilityImage'));
const AIScreenerImage = lazy(() => import('../components/responsibleAI/AIScreenerImage'));

const ResponsibleAI = () => {
  const [activeSection, setActiveSection] = useState('overview');
  const [scrollPosition, setScrollPosition] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setScrollPosition(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <Helmet>
        <title>Responsible AI at Cutshort</title>
        <meta name="description" content="Learn about how Cutshort implements responsible AI principles in recruiting" />
      </Helmet>
      
      <NavBar />
      
      {/* Hero Section with Gradient Background */}
      <div 
        className="relative pt-24 pb-16 px-4 md:px-8"
        style={{ 
          background: "linear-gradient(135deg, rgba(240,241,247,0.95) 0%, rgba(155,135,245,0.15) 100%)",
          minHeight: "40vh",
          display: "flex",
          alignItems: "center",
        }}
      >
        <div className="container mx-auto max-w-4xl z-10">
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="flex-1 space-y-4">
              <div className="inline-flex items-center px-3 py-1 rounded-full bg-brand-blue/10 text-brand-blue mb-4">
                <ShieldCheck className="w-4 h-4 mr-2" />
                <span className="text-sm font-medium">Our Commitment</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-brand-darkgrey leading-tight">
                Responsible AI <span className="text-brand-blue">at Cutshort</span>
              </h1>
              <p className="text-lg text-brand-grey max-w-2xl">
                At Cutshort, we believe in being transparent about how we use artificial intelligence (AI) in our recruitment platform. This document outlines our approach, safeguards, and commitment to ethical AI use.
              </p>
            </div>
          </div>
        </div>
        
        {/* Decorative elements */}
        <div className="absolute top-20 right-10 w-64 h-64 bg-brand-blue/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 left-10 w-48 h-48 bg-brand-yellow/10 rounded-full blur-3xl"></div>
      </div>

      {/* Main Content Section with Navigation */}
      <div className="container mx-auto px-4 md:px-8 py-12">
        <div className="flex flex-col lg:flex-row gap-8 max-w-6xl mx-auto">
          {/* Side Navigation - Visible on larger screens */}
          <div className="lg:w-1/4 hidden lg:block">
            <div className={`space-y-2 ${scrollPosition > 300 ? 'sticky top-24' : ''}`}>
              <h3 className="text-lg font-semibold text-brand-darkgrey mb-4">On this page</h3>
              <nav className="flex flex-col space-y-2">
                {[
                  { id: 'overview', label: 'Overview', icon: <Brain className="w-4 h-4" /> },
                  { id: 'notes', label: 'Important Notes', icon: <AlertCircle className="w-4 h-4" /> },
                  { id: 'screener', label: 'AI Screener', icon: <Users className="w-4 h-4" /> },
                  { id: 'privacy', label: 'Data Privacy', icon: <ShieldCheck className="w-4 h-4" /> },
                  { id: 'faq', label: 'FAQ', icon: <Lightbulb className="w-4 h-4" /> }
                ].map((item) => (
                  <button 
                    key={item.id}
                    onClick={() => {
                      setActiveSection(item.id);
                      document.getElementById(item.id)?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                      activeSection === item.id 
                        ? 'bg-brand-blue text-white' 
                        : 'text-brand-grey hover:bg-brand-blue/10 hover:text-brand-blue'
                    }`}
                  >
                    {item.icon}
                    <span>{item.label}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>
          
          {/* Main Content */}
          <div className="lg:w-3/4 space-y-16">
            {/* Overview Section */}
            <section id="overview" className="scroll-mt-24">
              <Card className="border-none shadow-lg overflow-hidden">
                <CardContent className="p-0">
                  <Suspense fallback={<div className="w-full h-64 bg-gray-100 animate-pulse rounded-t-lg"></div>}>
                    <AIResponsibilityImage />
                  </Suspense>
                  
                  <div className="p-6 sm:p-8">
                    <h2 className="text-2xl font-bold text-brand-darkgrey mb-4 flex items-center">
                      <Brain className="mr-2 text-brand-blue" />
                      Overview
                    </h2>
                    <p className="text-gray-600">
                      At Cutshort, we believe in being transparent about how we use artificial intelligence (AI) in our recruitment platform. This document outlines our approach, safeguards, and commitment to ethical AI use.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </section>

            {/* Important Notes Section */}
            <section id="notes" className="scroll-mt-24">
              <Card className="border shadow-md">
                <CardHeader className="bg-brand-offwhite">
                  <CardTitle className="flex items-center">
                    <AlertCircle className="mr-2 text-brand-blue" />
                    Important Notes on AI Usage
                  </CardTitle>
                  <CardDescription>We want to be clear about how AI is used on the Cutshort platform</CardDescription>
                </CardHeader>
                <CardContent className="pt-6">
                  <Accordion type="single" collapsible className="w-full">
                    <AccordionItem value="item-1">
                      <AccordionTrigger className="text-brand-darkgrey hover:text-brand-blue">
                        AI's Role in Decision Making
                      </AccordionTrigger>
                      <AccordionContent className="text-brand-grey">
                        <ul className="list-disc pl-6 space-y-2">
                          <li>AI features are designed to increase relevance and save time for both recruiters and candidates</li>
                          <li>AI never makes final selection or rejection decisions</li>
                          <li>All hiring decisions are made by human recruiters</li>
                        </ul>
                      </AccordionContent>
                    </AccordionItem>
                    <AccordionItem value="item-2">
                      <AccordionTrigger className="text-brand-darkgrey hover:text-brand-blue">
                        Data Handling
                      </AccordionTrigger>
                      <AccordionContent className="text-brand-grey">
                        <ul className="list-disc pl-6 space-y-2">
                          <li>Recruiters have access to all original information submitted by candidates</li>
                          <li>AI analysis is supplementary and never replaces original candidate data such as resumes or answers to screening questions or AI interviews</li>
                        </ul>
                      </AccordionContent>
                    </AccordionItem>
                    <AccordionItem value="item-3">
                      <AccordionTrigger className="text-brand-darkgrey hover:text-brand-blue">
                        Diversity Considerations
                      </AccordionTrigger>
                      <AccordionContent className="text-brand-grey">
                        <p>
                          Note that a human recruiter using the AI system could, however, try to explicitly instruct AI to search or rank candidates based on these attributes (e.g. gender or age) to meet their Diversity and other hiring goals and we today allow it trusting the recruiters to use it for legally approved reasons.
                        </p>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </CardContent>
              </Card>
            </section>

            {/* AI Screener Section */}
            <section id="screener" className="scroll-mt-24">
              <Card className="border shadow-md overflow-hidden">
                <CardHeader className="bg-brand-offwhite">
                  <CardTitle className="flex items-center">
                    <Users className="mr-2 text-brand-blue" />
                    AI Screener: Non-Discrimination & Bias Prevention
                  </CardTitle>
                  <CardDescription>How we ensure fairness in our AI screening process</CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                  <Suspense fallback={<div className="w-full h-64 bg-gray-100 animate-pulse"></div>}>
                    <AIScreenerImage />
                  </Suspense>
                  
                  <div className="p-6">
                    <p className="text-gray-600 mb-4">
                      We have an AI Screener tool which invites a sample of job applicants for a technical discussion as per the key criteria added by the hiring company.
                    </p>
                    
                    <p className="text-gray-600 mb-4">
                      In this tool, we take active steps to prevent bias in our AI systems. By default, our AI models only look at professional work details and skills of candidates while considering them for any job positions and exclude considering information such as their:
                    </p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                      {[
                        { label: "Gender", icon: <Users className="w-4 h-4" /> },
                        { label: "Age", icon: <Clock className="w-4 h-4" /> },
                        { label: "Ethnicity", icon: <Users className="w-4 h-4" /> },
                        { label: "Wealth status", icon: <AlertCircle className="w-4 h-4" /> },
                        { label: "Appearances", icon: <Users className="w-4 h-4" /> },
                        { label: "Audio or video characteristics", icon: <AlertCircle className="w-4 h-4" /> }
                      ].map((item, index) => (
                        <div key={index} className="flex items-center p-3 bg-brand-offwhite rounded-lg">
                          <div className="mr-3 p-2 bg-white rounded-full text-brand-blue">
                            {item.icon}
                          </div>
                          <span className="text-sm font-medium text-brand-darkgrey">{item.label}</span>
                        </div>
                      ))}
                    </div>
                    
                    <p className="text-gray-600 mt-6">
                      We do regular bias testing and audits for this AI Screener tool.
                    </p>
                    
                    <p className="mt-4">
                      <a 
                        href="https://cutshort.io/a/bias-testing-report-latest" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-brand-blue hover:underline font-medium"
                      >
                        View latest bias testing report
                      </a>
                    </p>
                  </div>
                </CardContent>
              </Card>
            </section>

            {/* Data Privacy Section */}
            <section id="privacy" className="scroll-mt-24">
              <Card className="border shadow-md">
                <CardHeader className="bg-brand-offwhite">
                  <CardTitle className="flex items-center">
                    <ShieldCheck className="mr-2 text-brand-blue" />
                    Data Privacy & Security
                  </CardTitle>
                  <CardDescription>How we protect candidate and customer data</CardDescription>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {[
                      { title: "Consent", description: "We take consent from candidates for AI processing", icon: <ShieldCheck className="w-8 h-8 text-brand-blue" /> },
                      { title: "Security", description: "Secure data storage with encryption", icon: <ShieldCheck className="w-8 h-8 text-brand-blue" /> },
                      { title: "Retention", description: "Limited data retention periods", icon: <Clock className="w-8 h-8 text-brand-blue" /> },
                      { title: "Rights", description: "Right to delete your data", icon: <Users className="w-8 h-8 text-brand-blue" /> },
                    ].map((item, index) => (
                      <div key={index} className="p-4 border rounded-lg hover:shadow-md transition-shadow">
                        <div className="flex flex-col items-center text-center">
                          <div className="mb-3">
                            {item.icon}
                          </div>
                          <h3 className="font-semibold text-brand-darkgrey mb-2">{item.title}</h3>
                          <p className="text-sm text-brand-grey">{item.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </section>
            
            {/* FAQ Section */}
            <section id="faq" className="scroll-mt-24">
              <Card className="border shadow-md">
                <CardHeader className="bg-brand-offwhite">
                  <CardTitle className="flex items-center">
                    <Lightbulb className="mr-2 text-brand-blue" />
                    Frequently Asked Questions
                  </CardTitle>
                  <CardDescription>Common questions about our AI practices</CardDescription>
                </CardHeader>
                <CardContent className="pt-6">
                  <Accordion type="single" collapsible className="w-full">
                    <AccordionItem value="item-1">
                      <AccordionTrigger>How does Cutshort ensure AI fairness?</AccordionTrigger>
                      <AccordionContent>
                        We conduct regular audits of our AI systems to detect and eliminate bias. Our AI models are designed to focus solely on professional qualifications and skills, deliberately excluding demographic information from consideration in the screening process.
                      </AccordionContent>
                    </AccordionItem>
                    <AccordionItem value="item-2">
                      <AccordionTrigger>Can I opt out of AI processing?</AccordionTrigger>
                      <AccordionContent>
                        Yes, candidates can opt out of AI processing at any time. We respect your privacy choices and provide alternative evaluation methods when requested.
                      </AccordionContent>
                    </AccordionItem>
                    <AccordionItem value="item-3">
                      <AccordionTrigger>How is my data used in the AI systems?</AccordionTrigger>
                      <AccordionContent>
                        Your professional information like skills, experience, and qualifications are used to match you with relevant opportunities. Personal demographic information is never used in our matching algorithms unless explicitly requested by recruiters for diversity initiatives permitted by law.
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </CardContent>
              </Card>
            </section>
          </div>
        </div>
      </div>

      <div className="bg-brand-offwhite py-16">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <h2 className="text-3xl font-bold text-brand-darkgrey">Have Questions About Our AI Practices?</h2>
            <p className="text-lg text-brand-grey">We're committed to transparency and responsible AI use. If you have any questions about our practices, please reach out.</p>
            <div className="flex justify-center pt-4">
              <a 
                href="mailto:privacy@cutshort.io" 
                className="inline-flex items-center space-x-2 px-6 py-3 bg-brand-blue text-white rounded-full hover:bg-brand-blue/90 transition-colors"
              >
                <Mail className="w-4 h-4" />
                <span>Contact Our Privacy Team</span>
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t py-8">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-3xl mx-auto text-center text-sm text-gray-500">
            <p>This document was last updated on April 15, 2025</p>
          </div>
        </div>
      </div>
      
      <MainFooter />
    </>
  );
};

export default ResponsibleAI;
